import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { magpCMSApi } from "~/Services";

interface WelcomePageForCalendar {
  id: number;
  title: string;
  field_malay_calendar_header_text: string;
  field_mandarin_calendar_header_t: string;
  field_welcome_calendar_text: string;
  field_malay_welcome_calendar_tex: string;
  field_mandarin_welcome_calendar: string;
  field_welcome_calendar_descripti: string;
  field_malay_welcome_calendar_des: string;
  field_mandari_welco_calen_descri: string;
  field_calendar_welcom_continue: string;
  field_malay_continue_text: string;
  field_mandarin_continue_text: string;
  field_magp_image: string;
}

interface CalendarLegends {
  name: string;
  field_start_day_ms: string;
  field_start_day_zh: string;
  field_start_day_color_code: string;
  field_current_day_en: string;
  field_current_day_ms: string;
  field_current_day_zh: string;
  field_current_day_color_code: string;
  field_exp_date_en: string;
  field_exp_date_ms: string;
  field_exp_date_zh: string;
  field_exp_day_color_code: string;
  exp_day_code: string;
  field_multi_en: string;
  field_multi_ms: string;
  field_multi_zh: string;
  field_multi_contract_code: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

interface WebBannerImages {
  nid: string;
  title_en: string;
  title_ms: string | boolean;
  title_zh: string | boolean;
  description_en: string | boolean;
  description_ms: string | boolean;
  description_zh: string | boolean;
  image: string;
  createdAt: string;
  updatedAt: string;
  published: boolean | string;
}

interface MobileHomepageBannerSlider {
  id: number;
  name_en: string;
  name_ms: string;
  name_zh: string;
  image: string;
  description_en: string;
  description_ms: string;
  description_zh: string;
  sequence: number;
  createdAt: string;
  updatedAt: string;
  published: boolean;
}

interface LoginFooterPage {
  id: string;
  title: string;
  button_list: {
    id: any;
    button_en: string;
    button_ms: string;
    button_zh: string;
    button_link: string;
  }[];
  copyright_text_en: string;
  copyright_text_ms: string;
  copyright_text_zh: string;
  image: string;
  createdAt: string;
  updatedAt: string;
  published: boolean;
}

const useWelcomePageForCalendar = (
  options?: UseQueryOptions<WelcomePageForCalendar[], DefaultError, WelcomePageForCalendar[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WelcomePageForCalendar[], DefaultError> => {
  return useQuery<WelcomePageForCalendar[], DefaultError, WelcomePageForCalendar[], QueryKey>(
    {
      queryKey: ["welcomePageForCalendar"],
      enabled: false,
      queryFn: async (): Promise<WelcomePageForCalendar[]> => {
        const response = await magpCMSApi.get<WelcomePageForCalendar[]>(`/api/calendar-welcome/all-fields`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useCalendarLegends = (
  options?: UseQueryOptions<CalendarLegends[], DefaultError, CalendarLegends[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<CalendarLegends[], DefaultError> => {
  return useQuery<CalendarLegends[], DefaultError, CalendarLegends[], QueryKey>(
    {
      queryKey: ["calendarLegends"],
      enabled: false,
      queryFn: async (): Promise<CalendarLegends[]> => {
        const response = await magpCMSApi.get<CalendarLegends[]>(`/api/get-color-codes/all-fields`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useWebBannerImages = (
  id?: any,
  options?: UseQueryOptions<WebBannerImages[], DefaultError, WebBannerImages[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WebBannerImages[], DefaultError> => {
  return useQuery<WebBannerImages[], DefaultError, WebBannerImages[], QueryKey>(
    {
      queryKey: ["webBannerImages", id],
      enabled: false,
      queryFn: async (): Promise<WebBannerImages[]> => {
        const response = await magpCMSApi.get<WebBannerImages[]>(`/api/banners-images-for-all-nodes-data-list/${id}`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useMobileHomepageBannerSlider = (
  id?: any,
  options?: UseQueryOptions<MobileHomepageBannerSlider[], DefaultError, MobileHomepageBannerSlider[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<MobileHomepageBannerSlider[], DefaultError> => {
  return useQuery<MobileHomepageBannerSlider[], DefaultError, MobileHomepageBannerSlider[], QueryKey>(
    {
      queryKey: ["mobileHomepageBannerSlider", id],
      enabled: false,
      queryFn: async (): Promise<MobileHomepageBannerSlider[]> => {
        const response = await magpCMSApi.get<MobileHomepageBannerSlider[]>(`/api/get-mobile-banner-images/${id}`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useGuestLoginFooterPage = (
  options?: UseQueryOptions<LoginFooterPage[], DefaultError, LoginFooterPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<LoginFooterPage[], DefaultError> => {
  return useQuery<LoginFooterPage[], DefaultError, LoginFooterPage[], QueryKey>(
    {
      queryKey: ["guestLoginFooterPage"],
      enabled: false,
      queryFn: async (): Promise<LoginFooterPage[]> => {
        const response = await magpCMSApi.get<LoginFooterPage[]>(`/api/footer-section-image-list-data-api/816`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useVendorLoginFooterPage = (
  options?: UseQueryOptions<LoginFooterPage[], DefaultError, LoginFooterPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<LoginFooterPage[], DefaultError> => {
  return useQuery<LoginFooterPage[], DefaultError, LoginFooterPage[], QueryKey>(
    {
      queryKey: ["vendorLoginFooterPage"],
      enabled: false,
      queryFn: async (): Promise<LoginFooterPage[]> => {
        const response = await magpCMSApi.get<LoginFooterPage[]>(`/api/footer-section-image-list-data-api/817`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useStaffLoginFooterPage = (
  options?: UseQueryOptions<LoginFooterPage[], DefaultError, LoginFooterPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<LoginFooterPage[], DefaultError> => {
  return useQuery<LoginFooterPage[], DefaultError, LoginFooterPage[], QueryKey>(
    {
      queryKey: ["staffLoginFooterPage"],
      enabled: false,
      queryFn: async (): Promise<LoginFooterPage[]> => {
        const response = await magpCMSApi.get<LoginFooterPage[]>(`/api/footer-section-image-list-data-api/815`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

export {
  useCalendarLegends,
  useGuestLoginFooterPage,
  useMobileHomepageBannerSlider,
  useStaffLoginFooterPage,
  useVendorLoginFooterPage,
  useWebBannerImages,
  useWelcomePageForCalendar,
};
